import React, { useState, useEffect } from 'react';
import { useTheme } from '../../../Context/ThemeContext';
import { PromotionManagementHandler, PromotionTranslations } from './PromotionManagementHandler';

interface PromotionFormModalProps {
    promotionId: string | null; // null = create mode
    onClose: () => void;
    onSaved: () => void;
}

const LANGUAGES: { code: string; name: string }[] = [
    { code: 'en', name: 'English' },
    { code: 'es', name: 'Español' },
    { code: 'fr', name: 'Français' },
    { code: 'hi', name: 'हिन्दी' },
    { code: 'bn', name: 'বাংলা' },
    { code: 'pt', name: 'Português' },
    { code: 'it', name: 'Italiano' },
    { code: 'ru', name: 'Русский' },
    { code: 'ja', name: '日本語' },
    { code: 'ko', name: '한국어' },
];

const emptyTranslations = (): PromotionTranslations =>
    LANGUAGES.reduce((acc, l) => ({ ...acc, [l.code]: '' }), {} as PromotionTranslations);

const toDateInput = (value: string | undefined): string => {
    if (!value) return '';
    // Accepts ISO ("2026-07-31T00:00:00+00:00") or "2026-07-31"
    return value.length >= 10 ? value.substring(0, 10) : value;
};

// "2026-07-31" -> "2026-07-31T00:00:00.000Z" so the backend can bind it to a
// DateTimeOffset (System.Text.Json rejects date-only strings).
const toIsoDate = (value: string): string => {
    if (!value) return '';
    const parsed = new Date(`${value}T00:00:00Z`);
    return isNaN(parsed.getTime()) ? value : parsed.toISOString();
};

// Drop blank language entries so only filled translations are stored (keeps the
// list view's language count accurate and avoids persisting empty strings).
const pruneEmpty = (dict: PromotionTranslations): PromotionTranslations =>
    Object.fromEntries(Object.entries(dict).filter(([, v]) => v && v.trim() !== ''));

const PromotionFormModal: React.FC<PromotionFormModalProps> = ({ promotionId, onClose, onSaved }) => {
    const { theme } = useTheme();
    const isEdit = promotionId !== null;

    const [title, setTitle] = useState<PromotionTranslations>(emptyTranslations());
    const [description, setDescription] = useState<PromotionTranslations>(emptyTranslations());
    const [requirements, setRequirements] = useState<PromotionTranslations>(emptyTranslations());

    const [reward, setReward] = useState('');
    const [prizeImageUrl, setPrizeImageUrl] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [maxClaims, setMaxClaims] = useState('20');
    const [priority, setPriority] = useState('1');
    const [active, setActive] = useState(true);

    const [activeLang, setActiveLang] = useState('en');
    const [loading, setLoading] = useState(isEdit);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!isEdit || !promotionId) return;
        const fetchData = async () => {
            setLoading(true);
            try {
                const handler = new PromotionManagementHandler();
                const d = await handler.getPromotionDetails(promotionId);
                setTitle({ ...emptyTranslations(), ...(d.title || {}) });
                setDescription({ ...emptyTranslations(), ...(d.description || {}) });
                setRequirements({ ...emptyTranslations(), ...(d.requirements || {}) });
                setReward(d.reward || '');
                setPrizeImageUrl(d.prizeImageUrl || '');
                setStartDate(toDateInput(d.startDate));
                setEndDate(toDateInput(d.endDate));
                setMaxClaims(String(d.maxClaims ?? 0));
                setPriority(String(d.priority ?? 1));
                setActive(d.active ?? true);
            } catch (err) {
                console.error('Error loading promotion:', err);
                setError('Failed to load promotion.');
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [isEdit, promotionId]);

    const setLangValue = (
        setter: React.Dispatch<React.SetStateAction<PromotionTranslations>>,
        lang: string,
        value: string,
    ) => {
        setter((prev) => ({ ...prev, [lang]: value }));
    };

    const handleSave = async () => {
        if (!title.en?.trim()) {
            setError('An English title is required.');
            setActiveLang('en');
            return;
        }
        if (!startDate || !endDate) {
            setError('Start and end dates are required.');
            return;
        }

        setSaving(true);
        setError(null);
        try {
            const handler = new PromotionManagementHandler();
            const payload = {
                title: pruneEmpty(title),
                description: pruneEmpty(description),
                requirements: pruneEmpty(requirements),
                reward: reward.trim(),
                prizeImageUrl: prizeImageUrl.trim(),
                startDate: toIsoDate(startDate),
                endDate: toIsoDate(endDate),
                maxClaims: parseInt(maxClaims, 10) || 0,
                active,
                priority: parseInt(priority, 10) || 0,
            };
            if (isEdit && promotionId) {
                await handler.updatePromotion(promotionId, payload);
            } else {
                await handler.createPromotion(payload);
            }
            onSaved();
        } catch (err) {
            console.error('Error saving promotion:', err);
            setError('Failed to save promotion. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    const styles: Record<string, React.CSSProperties> = {
        overlay: {
            position: 'fixed' as const,
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
        },
        modal: {
            background: theme.card,
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '640px',
            width: '92%',
            maxHeight: '88vh',
            overflowY: 'auto' as const,
            boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
        },
        header: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '20px',
        },
        title: { fontSize: '20px', fontWeight: 700, color: theme.text },
        closeButton: {
            background: 'none',
            border: 'none',
            fontSize: '22px',
            color: theme.textSecondary,
            cursor: 'pointer',
            padding: '4px 8px',
        },
        sectionTitle: {
            fontSize: '13px',
            fontWeight: 700,
            color: theme.textSecondary,
            textTransform: 'uppercase' as const,
            letterSpacing: '0.5px',
            margin: '8px 0 12px',
        },
        grid2: {
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '12px',
        },
        fieldGroup: { marginBottom: '14px' },
        label: {
            display: 'block',
            fontSize: '13px',
            fontWeight: 600,
            color: theme.text,
            marginBottom: '6px',
        },
        input: {
            width: '100%',
            padding: '10px 14px',
            fontSize: '14px',
            border: `1px solid ${theme.inputBorder}`,
            borderRadius: '6px',
            background: theme.inputBg,
            color: theme.text,
            outline: 'none',
            boxSizing: 'border-box' as const,
        },
        textarea: {
            width: '100%',
            padding: '10px 14px',
            fontSize: '14px',
            border: `1px solid ${theme.inputBorder}`,
            borderRadius: '6px',
            background: theme.inputBg,
            color: theme.text,
            outline: 'none',
            boxSizing: 'border-box' as const,
            minHeight: '70px',
            resize: 'vertical' as const,
            fontFamily: 'inherit',
        },
        tabRow: {
            display: 'flex',
            flexWrap: 'wrap' as const,
            gap: '6px',
            marginBottom: '14px',
            paddingBottom: '12px',
            borderBottom: `1px solid ${theme.border}`,
        },
        tab: {
            padding: '6px 12px',
            fontSize: '12px',
            fontWeight: 600,
            borderRadius: '6px',
            cursor: 'pointer',
            border: `1px solid ${theme.border}`,
            background: 'transparent',
            color: theme.textSecondary,
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
        },
        tabActive: {
            background: '#3b82f6',
            color: '#fff',
            border: '1px solid #3b82f6',
        },
        filledDot: {
            width: '6px',
            height: '6px',
            borderRadius: '50%',
            background: '#22c55e',
            display: 'inline-block',
        },
        hint: { fontSize: '11px', color: theme.textSecondary, marginTop: '4px' },
        divider: { height: '1px', background: theme.border, margin: '8px 0 18px' },
        checkboxRow: { display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' },
        checkbox: { width: '18px', height: '18px', cursor: 'pointer', accentColor: '#3b82f6' },
        footer: {
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '12px',
            marginTop: '8px',
            paddingTop: '16px',
            borderTop: `1px solid ${theme.border}`,
        },
        cancelButton: {
            padding: '10px 20px',
            fontSize: '14px',
            fontWeight: 500,
            color: theme.text,
            background: 'transparent',
            border: `1px solid ${theme.border}`,
            borderRadius: '6px',
            cursor: 'pointer',
        },
        saveButton: {
            padding: '10px 20px',
            fontSize: '14px',
            fontWeight: 600,
            color: '#fff',
            background: '#3b82f6',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            opacity: saving ? 0.6 : 1,
        },
        errorText: { color: '#ef4444', fontSize: '14px', marginBottom: '12px' },
        spinner: {
            display: 'inline-block',
            width: '16px',
            height: '16px',
            border: '2px solid rgba(59, 130, 246, 0.3)',
            borderTop: '2px solid #3b82f6',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
        },
    };

    return (
        <div style={styles.overlay} onClick={onClose}>
            <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
            <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
                <div style={styles.header}>
                    <span style={styles.title}>{isEdit ? 'Edit Promotion' : 'Create Promotion'}</span>
                    <button style={styles.closeButton} onClick={onClose}>&times;</button>
                </div>

                {error && <div style={styles.errorText}>{error}</div>}

                {loading ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '24px 0' }}>
                        <div style={styles.spinner} />
                        <span style={{ color: theme.textSecondary, fontSize: '13px' }}>Loading promotion...</span>
                    </div>
                ) : (
                    <>
                        <div style={styles.sectionTitle}>Settings</div>
                        <div style={styles.grid2}>
                            <div style={styles.fieldGroup}>
                                <label htmlFor="promo-reward" style={styles.label}>Reward</label>
                                <input
                                    id="promo-reward"
                                    type="text"
                                    placeholder="$10 Amazon Gift Card"
                                    value={reward}
                                    onChange={(e) => setReward(e.target.value)}
                                    style={styles.input}
                                />
                            </div>
                            <div style={styles.fieldGroup}>
                                <label htmlFor="promo-prize" style={styles.label}>Prize Image URL</label>
                                <input
                                    id="promo-prize"
                                    type="text"
                                    placeholder="https://..."
                                    value={prizeImageUrl}
                                    onChange={(e) => setPrizeImageUrl(e.target.value)}
                                    style={styles.input}
                                />
                            </div>
                            <div style={styles.fieldGroup}>
                                <label htmlFor="promo-startDate" style={styles.label}>Start Date</label>
                                <input id="promo-startDate" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} style={styles.input} />
                            </div>
                            <div style={styles.fieldGroup}>
                                <label htmlFor="promo-endDate" style={styles.label}>End Date</label>
                                <input id="promo-endDate" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} style={styles.input} />
                            </div>
                            <div style={styles.fieldGroup}>
                                <label htmlFor="promo-maxClaims" style={styles.label}>Max Claims</label>
                                <input id="promo-maxClaims" type="number" min="0" value={maxClaims} onChange={(e) => setMaxClaims(e.target.value)} style={styles.input} />
                            </div>
                            <div style={styles.fieldGroup}>
                                <label htmlFor="promo-priority" style={styles.label}>Priority (lower shows first)</label>
                                <input id="promo-priority" type="number" min="0" value={priority} onChange={(e) => setPriority(e.target.value)} style={styles.input} />
                            </div>
                        </div>

                        <div style={styles.checkboxRow}>
                            <input
                                type="checkbox"
                                checked={active}
                                onChange={(e) => setActive(e.target.checked)}
                                style={styles.checkbox}
                                id="promo-active"
                            />
                            <label htmlFor="promo-active" style={{ ...styles.label, marginBottom: 0, cursor: 'pointer' }}>
                                Active (visible on the website)
                            </label>
                        </div>

                        <div style={styles.divider} />

                        <div style={styles.sectionTitle}>Localized Content</div>
                        <div style={styles.tabRow}>
                            {LANGUAGES.map((l) => (
                                <button
                                    key={l.code}
                                    type="button"
                                    style={{ ...styles.tab, ...(activeLang === l.code ? styles.tabActive : {}) }}
                                    onClick={() => setActiveLang(l.code)}
                                >
                                    {l.name}
                                    {title[l.code]?.trim() ? <span style={styles.filledDot} /> : null}
                                </button>
                            ))}
                        </div>

                        <div style={styles.fieldGroup}>
                            <label htmlFor="promo-title" style={styles.label}>Title ({activeLang})</label>
                            <input
                                id="promo-title"
                                type="text"
                                value={title[activeLang] || ''}
                                onChange={(e) => setLangValue(setTitle, activeLang, e.target.value)}
                                style={styles.input}
                            />
                        </div>
                        <div style={styles.fieldGroup}>
                            <label htmlFor="promo-description" style={styles.label}>Description ({activeLang})</label>
                            <textarea
                                id="promo-description"
                                value={description[activeLang] || ''}
                                onChange={(e) => setLangValue(setDescription, activeLang, e.target.value)}
                                style={styles.textarea}
                            />
                        </div>
                        <div style={styles.fieldGroup}>
                            <label htmlFor="promo-requirements" style={styles.label}>Requirements ({activeLang})</label>
                            <textarea
                                id="promo-requirements"
                                value={requirements[activeLang] || ''}
                                onChange={(e) => setLangValue(setRequirements, activeLang, e.target.value)}
                                style={styles.textarea}
                            />
                            <div style={styles.hint}>Separate each line with &quot; | &quot; (space-pipe-space). Example: 1. Item one | 2. Item two</div>
                        </div>
                    </>
                )}

                <div style={styles.footer}>
                    <button style={styles.cancelButton} onClick={onClose} disabled={saving}>
                        Cancel
                    </button>
                    <button style={styles.saveButton} onClick={handleSave} disabled={saving || loading}>
                        {saving ? 'Saving...' : isEdit ? 'Update Promotion' : 'Create Promotion'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default PromotionFormModal;
